import React, { useEffect, useRef, useState } from "react";
import { AddLayout } from "../../../layouts";
import {
  Autocomplete,
  Box,
  CircularProgress,
  Grid,
  TextField,
  DatePicker,
  Select,
  MenuItem,
  Typography,
} from "../../../components";
import {
  formatPrice,
  responseErros,
  formatDate,
  montarFiltroPessoa,
} from "../../../utils";
import {
  history,
  NfseService,
  notification,
  PessoaService,
  ConfiguracoesOrdemServicoService,
} from "../../../services";
import NfseEntity from "./entities/nfse.entity";
import { InputErros, value, PermisoesHelper } from "../../../helpers";
import { nfseValidator, nfseEditarValidator } from "./middlewares";
import {
  ButtonMenu,
  ListaServicosNfse,
  ErrosEmitirDialog,
  CancelamentoDialog,
} from "./components";
import "./add-nfse.scss";

const permissoesHelper = new PermisoesHelper();
// const nfeService = new NfeService();
const nfseService = new NfseService();
const pessoaService = new PessoaService();
const configuracoesOrdemServicoService = new ConfiguracoesOrdemServicoService();
const status = [
  {
    nome: "ENVIADA",
    label: "Enviada",
    cor: "#4FBB53",
  },
  {
    nome: "PENDENTE",
    label: "Pendente",
    cor: "#EA9D2B",
  },
  {
    nome: "CANCELADA",
    label: "Cancelada",
    cor: "#DC3545",
  },
];

const AddNfse = ({ match }) => {
  const id = match?.params?.id;
  permissoesHelper.validarPermisao("ordem-servicos-visualizar");
  const [loading, setLoading] = useState(false);
  const [loadingAutoComplete, setLoadingAutoComplete] = useState(false);
  const [nfse, setNfse] = useState(new NfseEntity({}));
  const [clienteList, setClienteList] = useState([]);
  const [inputErro, setInputErro] = useState([]);
  const [errosEmitirDialogOpen, setErrosEmitirDialogOpen] = useState(false);
  const [errosEmitir, setErrosEmitir] = useState(null);
  const [openCancelamentoDialog, setOpenCancelamentoDialog] = useState(false);
  const inputErros = new InputErros(inputErro, setInputErro);
  const inputCliente = useRef();
  const [servicos, setServicos] = useState([]);
  // const sessionService = new SessionService();

  useEffect(() => {
    buscarDadosIniciais();
  }, []);

  const buscarDadosIniciais = async () => {
    await buscarNfse();
    await buscarSerieDocumentoConfiguracao();
  };

  const buscarImportacao = async (nfseData) => {
    if (window.nfSeEntity) {
      const imported = window.nfSeEntity;
      if (imported.servicos) {
        setNfse({
          ...nfseData,
          ...imported,
        });
        setServicos(imported.servicos || []);
        buscarClientes({
          id: imported.clienteId,
        });
      }
      try {
        delete window.nfSeEntity;
      } catch (e) {
        window.nfSeEntity = undefined;
      }
    }
  };

  const totalServicos = (servicos || []).reduce((acc, servico) => {
    const totalItem = Number(servico?.totalItem);
    if (!Number.isNaN(totalItem)) {
      return acc + totalItem;
    }

    const quantidade = Number(servico?.quantidade || 0);
    const valor = Number(servico?.valor || 0);
    return acc + quantidade * valor;
  }, 0);

  const buscarNfse = async () => {
    if (!id) return;
    setLoading(true);
    const response = await nfseService.getById(id);
    setLoading(false);
    if (!response.isAxiosError) {
      setNfse(response.data);
      setServicos(response.data.servicos);
      buscarClientes({
        id: response.data.clienteId,
      });
    } else {
      responseErros(response);
    }
  };

  const buscarSerieDocumentoConfiguracao = async () => {
    if (id) return;
    const response = await configuracoesOrdemServicoService.getAll();
    if (!response.isAxiosError) {
      const configuracao = response.data[0];
      const nfseData = {
        ...nfse,
        serieDocumento: configuracao?.serieNfse || "",
      };
      setNfse(nfseData);
      await buscarImportacao(nfseData);
    } else {
      responseErros(response);
    }
  };

  const buscarClientes = async (filtros) => {
    const filtro = {
      ...filtros,
      tiposPessoaId: 1,
      nonPaginated: true,
      ativado: true,
    };
    setLoadingAutoComplete(true);
    const result = await pessoaService.getAll(filtro);
    setLoadingAutoComplete(false);
    if (!result.isAxiosError) {
      setClienteList(result.data || []);
    } else {
      responseErros(result);
    }
  };

  const onSearchChangeClientes = async (event) => {
    if (
      event.key === "Enter" &&
      inputCliente.current === document.activeElement
    ) {
      const value = event.target?.value || "";
      if (!value.trim()) return;
      await buscarClientes(montarFiltroPessoa(value));
    }
  };

  const onChangeAutocompleteCliente = (newValue) => {
    setInputErro([]);
    setNfse((prev) => ({
      ...prev,
      clienteId: newValue?.id || null,
      clienteNome: newValue?.nomeRazaoSocial || "",
      clienteCnpjCpf: newValue?.cnpjCpf || "",
    }));
  };

  const validarCadastro = async (nfseEntity, indiceSelecionado) => {
    nfseValidator
      .validate(nfseEntity, { abortEarly: false })
      .then(async () => {
        setLoading(true);
        const response = await nfseService.cadastrar(nfseEntity);
        setLoading(false);
        if (!response.isAxiosError) {
          notification.cadastroSucesso();
          if (indiceSelecionado === 0) {
            await emitirAposCadastro(response.data);
          } else {
            limparFormularioListagem();
          }
        } else {
          responseErros(response);
        }
      })
      .catch((err) => {
        inputErros.set(err);
      });
  };

  async function emitirAposCadastro(nfseData) {
    const props = {
      title: "Documento cadastrado com sucesso",
      text: `#${nfseData.numeroDocumento}`,
      icon: "success",
      confirmButtonText: "EMITIR",
      cancelButtonText: "FECHAR",
      showCancelButton: true,
    };
    const confirmacaoAlerta = await notification.confirmacaoGenericos(props);
    if (confirmacaoAlerta.isConfirmed) {
      await emitirNfse(nfseData?.id);
      history.push(`/faturamento/nfse/${nfseData?.id}`);
    } else {
      history.push("/faturamento/nfse");
    }
  }

  function limparFormularioListagem() {
    setNfse(new NfseEntity({}));
    setServicos([]);
    setInputErro([]);
  }

  function validarEditar(nfseEntity) {
    nfseEditarValidator
      .validate(nfseEntity, { abortEarly: false })
      .then(async () => {
        setLoading(true);
        const response = await nfseService.atualizar(id, nfseEntity);
        setLoading(false);
        if (!response.isAxiosError) {
          notification.alteracaoSucesso();
          history.push("/faturamento/nfse");
        } else {
          responseErros(response);
        }
      })
      .catch((err) => {
        inputErros.set(err);
      });
  }

  async function handleSubmit(indiceSelecionado) {
    const nfseEntity = new NfseEntity(nfse);
    if (id) {
      validarEditar(nfseEntity);
    } else {
      nfseEntity.setSerieDocumento(nfse);
      nfseEntity.setServicos(servicos);
      validarCadastro(nfseEntity, indiceSelecionado);
    }
  }

  const getStatusCircleClassName = (statusNome) => {
    const statusKey = (statusNome || "").toLowerCase();
    return `ph-fill ph-circle nfse-status-icon nfse-status-icon--${statusKey}`;
  };

  const openAbaDanfeBase64 = (pdfBase64) => {
    const byteCharacters = atob(pdfBase64 || "");
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i += 1) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    const file = new Blob([byteArray], { type: "application/pdf;base64" });
    const fileURL = URL.createObjectURL(file);
    window.open(fileURL);
  };

  const onClickEmitirNfse = async () => {
    if (!id || loading) return;
    const notaEmitida = await emitirNfse(id);
    if (notaEmitida) {
      await buscarNfse();
    }
  };

  async function emitirNfse(idNfse) {
    setLoading(true);
    const response = await nfseService.emitirNotaFiscal(idNfse);
    setLoading(false);
    if (!response.isAxiosError) {
      return alertaRetornoEmitirNota(response.data);
    }
    responseErros(response);
    return false;
  }

  function alertaRetornoEmitirNota(data) {
    if (data?.sucesso) {
      notification.sucessoGenericos("NFS-e emitida com sucesso");
      return true;
    }
    handleDialogErrorEmitir(data.mensagens[0]);
    return false;
  }

  function handleDialogErrorEmitir(mensagem) {
    setErrosEmitir(mensagem);
    setErrosEmitirDialogOpen(true);
  }

  const onClickVisualizarNfse = async () => {
    if (!id || loading) return;
    setLoading(true);
    // const empresa = sessionService.getEmpresa();
    const response = await nfseService.getDanfe(id);
    // const response = await nfeService.baixarDanfeNfse(
    //   empresa.cnpjCpf,
    //   nfse.chaveAcesso,
    // );
    setLoading(false);
    if (!response.isAxiosError) {
      openAbaDanfeBase64(response?.data);
      return;
    }
    responseErros(response);
  };

  const permisaoEditar = () => {
    if (!permissoesHelper.temPermisao("ordem-servicos-editar")) {
      return false;
    }
    return !formularioDesabilitado();
  };

  const formularioDesabilitado = () => {
    if (nfse?.status === "CANCELADA" || nfse?.status === "ENVIADA") return true;
    return false;
  };

  const onClickBaixarXml = async () => {
    setLoading(true);
    const result = await nfseService.getArquivoXml(id);
    if (!result.isAxiosError) {
      const blob = new Blob([result.data], { type: "text/xml" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${nfse.chaveAcesso}.xml`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  const handleDateChange = (name, date) => {
    if (!date) {
      date = "Invalid Date";
    }
    setNfse((prevState) => ({
      ...prevState,
      [name]: date != "Invalid Date" ? formatDate.toSend(date) : null,
    }));
  };

  return (
    <AddLayout
      id={id}
      title={id ? "Editar NFS-e" : "Cadastrar NFS-e"}
      loading={loading}
      goBack={() => history.push("/faturamento/nfse")}
      hideConfigButton
      hideDeleteButton
      showUpdate={permisaoEditar()}
      ButtonMenu={
        <ButtonMenu
          status={nfse?.status}
          onClickEmitir={onClickEmitirNfse}
          onClickVisualizar={onClickVisualizarNfse}
          onClickCancelar={() => setOpenCancelamentoDialog(true)}
          onClickBaixarXml={onClickBaixarXml}
        />
      }
      onClickSalvar={handleSubmit}
      actionsTotalizadores={
        <Box className="nfse-actions-totalizadores">
          <div className="nfse-resumo-item">
            <Typography className="nfse-resumo-label">
              Total Serviços
            </Typography>
            <Box className="nfse-resumo-value">
              {formatPrice(totalServicos)}
            </Box>
          </div>
        </Box>
      }
    >
      <Grid container spacing={2}>
        <Grid item xs={1}>
          <TextField
            fullWidth
            disabled
            label="Série"
            value={nfse?.serieDocumento || ""}
          />
        </Grid>
        <Grid item xs={2}>
          <TextField
            fullWidth
            disabled
            label="Nº Documento"
            value={nfse?.numeroDocumento || ""}
          />
        </Grid>
        <Grid item xs={7}>
          <Autocomplete
            id="clienteId"
            name="clienteId"
            options={clienteList}
            autoHighlight
            disabled={formularioDesabilitado()}
            getOptionLabel={(option) => {
              if (!option) return "";
              if (option.codigo) {
                return `${option.codigo} - ${option.nomeRazaoSocial}`;
              }
              return option.nomeRazaoSocial || "";
            }}
            loading={loadingAutoComplete}
            loadingText="Carregando"
            noOptionsText={
              document.getElementById("clienteId")?.value?.length >= 1 && !id
                ? "Nenhum cliente encontrado"
                : "Digite e pressione Enter"
            }
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label="Cliente"
                required
                error={inputErros.get("clienteId")}
                onKeyDown={onSearchChangeClientes}
                inputRef={inputCliente}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loadingAutoComplete ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
            onChange={(_, newValue) => onChangeAutocompleteCliente(newValue)}
            value={value.autoComplete(clienteList, nfse.clienteId) || null}
          />
        </Grid>
        <Grid item xs={2}>
          <Select
            id="status"
            name="status"
            label="Status"
            variant="outlined"
            fullWidth
            disableClearable
            value={nfse?.status || ""}
          >
            {status.map((status, index) => (
              <MenuItem key={index} value={status.nome}>
                <div className="d-flex align-items-center justify-content-between">
                  {status.label}&nbsp;
                  {status?.cor && (
                    <i
                      className={getStatusCircleClassName(status.nome)}
                      style={{ color: status.cor }}
                    ></i>
                  )}
                </div>
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={3}>
          <DatePicker
            id="dataPrestacao"
            name="dataPrestacao"
            label="Data Prestação Serviço"
            disabled={formularioDesabilitado()}
            format="dd/MM/yyyy"
            value={value.date(nfse?.dataPrestacao)}
            onChange={(date) => handleDateChange("dataPrestacao", date)}
          />
        </Grid>
        <Grid item xs={3}>
          <DatePicker
            id="dataEmissao"
            name="dataEmissao"
            label="Data de Emissão"
            format="dd/MM/yyyy"
            disabled={true}
            value={value.date(nfse?.dataEmissao)}
          />
        </Grid>
      </Grid>
      <hr className="mt-4 mb-2" />
      <ListaServicosNfse
        formularioDesabilitado={formularioDesabilitado}
        servicos={servicos}
        setServicos={setServicos}
        id={id}
        buscarNfse={buscarNfse}
        setLoading={setLoading}
        nfse={nfse}
        setNfse={setNfse}
        inputErros={inputErros}
      />
      <ErrosEmitirDialog
        open={errosEmitirDialogOpen}
        setOpen={setErrosEmitirDialogOpen}
        errosEmitir={errosEmitir}
      />
      <CancelamentoDialog
        open={openCancelamentoDialog}
        setOpen={setOpenCancelamentoDialog}
        setLoading={setLoading}
        id={id}
        buscarNfse={buscarNfse}
      />
    </AddLayout>
  );
};

export default AddNfse;
